/**
 * Painel de Análise de Sentimento
 * Exibe sentimentos detectados por segmento com visualizações
 */

import React, { useState, useEffect, useCallback, memo } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt'
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral'
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import RefreshIcon from '@mui/icons-material/Refresh'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import { SentimentAnalysis, SentimentLabel, SegmentSentiment } from '../../../types/transcription'
import { getSentimentAnalysis, runOnDemandAnalysis } from '../../../services/api'
import { contextualErrors, FriendlyError } from '../../../services/errorHandler'
import AnalysisNotAvailableCard from './AnalysisNotAvailableCard'
import AnalysisSkippedCard from './AnalysisSkippedCard'
import AnalysisProcessingCard from './AnalysisProcessingCard'
import AnalysisOutdatedBanner from './AnalysisOutdatedBanner'
import HybridAnalysisList from './HybridAnalysisList'
import { SpeakerMapping } from '../../../services/exportService'

type Props = {
  fileId: string
  onNavigateToSegment?: (segmentIndex: number) => void
  speakerMapping?: SpeakerMapping
  /** Status atual desta análise (do polling) */
  analysisTypeStatus?: string
  /** Callback quando análise on-demand é iniciada */
  onAnalysisStarted?: () => void
  /** Flag indicando que segmentos foram editados após esta análise */
  analysisOutdated?: boolean
}

const SENTIMENT_CONFIG = {
  positive: {
    label: 'Positivo',
    icon: SentimentSatisfiedAltIcon,
    color: '#4caf50',
    bgColor: '#e8f5e9'
  },
  neutral: {
    label: 'Neutro',
    icon: SentimentNeutralIcon,
    color: '#9e9e9e',
    bgColor: '#fafafa'
  },
  negative: {
    label: 'Negativo',
    icon: SentimentDissatisfiedIcon,
    color: '#f44336',
    bgColor: '#ffebee'
  }
}

function SentimentIcon({ sentiment, size = 24 }: { sentiment: SentimentLabel; size?: number }) {
  const config = SENTIMENT_CONFIG[sentiment]
  const Icon = config.icon
  return <Icon sx={{ color: config.color, fontSize: size }} />
}

function SentimentBar({ value, label, color, compact = false }: { value: number; label: string; color: string; compact?: boolean }) {
  const safeValue = value ?? 0
  return (
    <Box sx={{ mb: compact ? 1.5 : 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: compact ? '0.75rem' : '0.875rem' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color, fontSize: compact ? '0.75rem' : '0.875rem' }}>
          {safeValue.toFixed(1)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={safeValue}
        sx={{
          height: compact ? 8 : 10,
          borderRadius: 5,
          bgcolor: '#f0f0f0',
          '& .MuiLinearProgress-bar': {
            bgcolor: color,
            borderRadius: 5
          },
          '#monkee-file-transcript-client &': {
            backgroundColor: '#f0f0f0 !important',
            '& .MuiLinearProgress-bar': {
              backgroundColor: `${color} !important`,
            }
          }
        }}
      />
    </Box>
  )
}

function ScoreGauge({ score, compact = false }: { score: number; compact?: boolean }) {
  const safeScore = score ?? 0
  // score de -1 a 1, convertemos para 0-100
  const percentage = ((safeScore + 1) / 2) * 100
  const color = safeScore > 0.2 ? '#4caf50' : safeScore < -0.2 ? '#f44336' : '#ff9800'
  const size = compact ? 90 : 120
  const thickness = compact ? 5 : 6

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{
          color: '#f0f0f0',
          '#monkee-file-transcript-client &': {
            color: '#f0f0f0 !important',
            '& .MuiCircularProgress-circle': { stroke: '#f0f0f0 !important' }
          }
        }}
      />
      <CircularProgress
        variant="determinate"
        value={percentage}
        size={size}
        thickness={thickness}
        sx={{
          color,
          position: 'absolute',
          left: 0,
          '#monkee-file-transcript-client &': {
            color: `${color} !important`,
            '& .MuiCircularProgress-circle': { stroke: `${color} !important` }
          }
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography sx={{
          fontWeight: 700,
          color,
          fontSize: compact ? '1.1rem' : '1.5rem',
          '#monkee-file-transcript-client &': { color: `${color} !important` }
        }}>
          {safeScore > 0 ? '+' : ''}{safeScore.toFixed(2)}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666', fontSize: compact ? '0.65rem' : '0.75rem' }}>
          Score Médio
        </Typography>
      </Box>
    </Box>
  )
}

function SegmentCardBase({ segment, onNavigateToSegment, style, compact = false, speakerMapping = {} }: {
  segment: SegmentSentiment
  onNavigateToSegment?: (segmentIndex: number) => void
  style?: React.CSSProperties
  compact?: boolean
  speakerMapping?: SpeakerMapping
}) {
  const config = SENTIMENT_CONFIG[segment.sentiment] || SENTIMENT_CONFIG.neutral
  const safeKeywords = segment.keywords ?? []
  const safeScore = segment.score ?? 0
  const safeConfidence = segment.confidence ?? 0
  // Limit keywords on mobile
  const displayKeywords = compact ? safeKeywords.slice(0, 3) : safeKeywords
  const hiddenKeywordsCount = compact ? safeKeywords.length - 3 : 0

  // Obter nome mapeado do speaker
  const speakerName = speakerMapping[segment.speaker] || segment.speaker

  return (
    <div style={{ ...style, paddingRight: compact ? 4 : 8, paddingBottom: compact ? 4 : 4 }}>
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.25 : 2,
        height: 'calc(100% - 4px)',
        boxSizing: 'border-box',
        border: '1px solid #e0e0e0',
        borderLeft: compact ? '3px solid' : '4px solid',
        borderLeftColor: config.color,
        bgcolor: config.bgColor,
        overflow: 'hidden',
        '&:hover': {
          borderColor: config.color,
          boxShadow: 1
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: compact ? 1 : 1.5 }}>
        <SentimentIcon sentiment={segment.sentiment} size={compact ? 22 : 28} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.5 : 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={speakerName}
              size="small"
              sx={{ fontWeight: 500, fontSize: compact ? '0.6rem' : '0.7rem', height: compact ? 20 : 24 }}
            />
            <Typography variant="caption" sx={{ color: '#666', fontSize: compact ? '0.6rem' : '0.75rem' }}>
              {formatTime(segment.startTime)}
            </Typography>
            {!compact && (
              <Tooltip title="Confiança do modelo de IA na classificação do sentimento deste segmento. Calculada pelo modelo com base na clareza e consistência do texto: valores acima de 80% indicam classificação segura; abaixo de 60% sugere texto ambíguo ou neutro com elementos mistos.">
                <Chip
                  label={`${(safeConfidence * 100).toFixed(0)}% conf. sentimento`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', cursor: 'help' }}
                />
              </Tooltip>
            )}
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: '#333',
              lineHeight: compact ? 1.4 : 1.5,
              mb: compact ? 0.5 : 1,
              fontSize: compact ? '0.75rem' : '0.875rem',
              display: '-webkit-box',
              WebkitLineClamp: compact ? 2 : undefined,
              WebkitBoxOrient: 'vertical',
              overflow: compact ? 'hidden' : 'visible'
            }}
          >
            "{segment.text}"
          </Typography>

          {displayKeywords.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {!compact && (
                <Typography variant="caption" sx={{ color: '#666', mr: 0.5 }}>
                  Palavras-chave:
                </Typography>
              )}
              {displayKeywords.map((kw, i) => (
                <Chip
                  key={i}
                  label={kw}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: compact ? '0.55rem' : '0.65rem', height: compact ? 18 : 20 }}
                />
              ))}
              {hiddenKeywordsCount > 0 && (
                <Typography variant="caption" sx={{ color: '#999', fontSize: '0.55rem' }}>
                  +{hiddenKeywordsCount}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: config.color,
              fontSize: compact ? '0.9rem' : '1.25rem'
            }}
          >
            {safeScore > 0 ? '+' : ''}{safeScore.toFixed(2)}
          </Typography>
          {onNavigateToSegment && (
            <Tooltip title="Ir para o segmento" disableHoverListener={compact}>
              <IconButton
                size="small"
                onClick={() => onNavigateToSegment(segment.segmentIndex)}
                sx={{
                  color: config.color,
                  p: compact ? 0.25 : 0.5,
                  '&:hover': { bgcolor: config.bgColor }
                }}
              >
                <ArrowForwardIcon sx={{ fontSize: compact ? 16 : 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
    </div>
  )
}

// Memoizar SegmentCard para evitar re-renders
const SegmentCard = memo(SegmentCardBase, (prev, next) => {
  return prev.segment.segmentIndex === next.segment.segmentIndex &&
         prev.segment.score === next.segment.score &&
         prev.compact === next.compact
})

/**
 * Formata o tempo para exibição.
 * Aceita tanto segundos (número ou string numérica) quanto formato HH:MM:SS.
 */
function formatTime(time: string): string {
  if (!time) return '0:00'
  
  // Se já está no formato HH:MM:SS ou MM:SS, retorna diretamente (ou simplificado)
  if (time.includes(':')) {
    const parts = time.split(':')
    if (parts.length === 3) {
      // HH:MM:SS - remover horas se for 00
      const hours = parseInt(parts[0], 10)
      const mins = parseInt(parts[1], 10)
      const secs = parseInt(parts[2], 10)
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    // MM:SS - retorna como está
    return time
  }
  
  // Se for número de segundos
  const seconds = parseFloat(time)
  if (isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function SentimentAnalysisPanel({ fileId, onNavigateToSegment, speakerMapping = {}, analysisTypeStatus, onAnalysisStarted, analysisOutdated }: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [analysis, setAnalysis] = useState<SentimentAnalysis | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [filter, setFilter] = useState<SentimentLabel | 'all'>('all')
  const [sentimentFullscreen, setSentimentFullscreen] = useState(false)
  const [reanalyzing, setReanalyzing] = useState(false)

  const handleReanalyze = async () => {
    setReanalyzing(true)
    try {
      await runOnDemandAnalysis(fileId, 'sentiment')
      onAnalysisStarted?.()
    } finally {
      setReanalyzing(false)
    }
  }

  const loadAnalysis = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getSentimentAnalysis(fileId)
      setAnalysis(data)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
      console.error('Erro ao carregar análise de sentimento:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Não busca dados se a análise foi ignorada ou está em processamento
    if (analysisTypeStatus === 'SKIPPED' || analysisTypeStatus === 'PROCESSING') return
    loadAnalysis()
  }, [fileId, analysisTypeStatus])

  // Se a análise foi ignorada, mostra card para executar sob demanda
  if (analysisTypeStatus === 'SKIPPED') {
    return (
      <AnalysisSkippedCard
        title="Análise de Sentimento"
        analysisType="sentiment"
        fileId={fileId}
        onAnalysisStarted={onAnalysisStarted}
      />
    )
  }

  // Se a análise está sendo processada, mostra card de processamento com animação
  if (analysisTypeStatus === 'PROCESSING') {
    return <AnalysisProcessingCard title="Análise de Sentimento" />
  }

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
          <CircularProgress size={isMobile ? 36 : 48} />
          <Typography sx={{ mt: 2, color: '#666', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            {isMobile ? 'Analisando...' : 'Analisando sentimentos...'}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
          <Box sx={{ fontSize: { xs: 36, sm: 48 }, mb: { xs: 1.5, sm: 2 } }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {error.title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {error.message}
          </Typography>
          {error.canRetry && (
            <Button
              variant="outlined"
              onClick={loadAnalysis}
              startIcon={<RefreshIcon />}
              size={isMobile ? 'small' : 'medium'}
            >
              {isMobile ? 'Tentar' : 'Tentar novamente'}
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  if (!analysis) {
    return (
      <AnalysisNotAvailableCard 
        title="Análise de Sentimento" 
        onRetry={loadAnalysis} 
      />
    )
  }

  // Valores seguros com fallbacks
  const overall = analysis.overall ?? { positive: 0, neutral: 0, negative: 0, averageScore: 0 }
  const segments = analysis.segments ?? []

  const filteredSegments = filter === 'all'
    ? segments
    : segments.filter(s => s.sentiment === filter)

  const trend = overall.averageScore > 0.1 
    ? 'up' 
    : overall.averageScore < -0.1 
    ? 'down' 
    : 'flat'

  return (
    <Box>
      {analysisOutdated && (
        <AnalysisOutdatedBanner
          analysisType="sentiment"
          onReanalyze={handleReanalyze}
          isReanalyzing={reanalyzing}
        />
      )}
      {/* Resumo Geral */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
              😊 {isMobile ? 'Sentimento' : 'Análise de Sentimento'}
            </Typography>
            <Tooltip title="Atualizar análise" disableHoverListener={isMobile}>
              <IconButton onClick={loadAnalysis} size="small">
                <RefreshIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Grid container spacing={{ xs: 2, sm: 4 }} alignItems="center">
            {/* Gauge Central */}
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <ScoreGauge score={overall.averageScore} compact={isMobile} />
              <Box sx={{ mt: { xs: 1, sm: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                {trend === 'up' && <TrendingUpIcon sx={{ color: '#4caf50', fontSize: { xs: 18, sm: 24 } }} />}
                {trend === 'down' && <TrendingDownIcon sx={{ color: '#f44336', fontSize: { xs: 18, sm: 24 } }} />}
                {trend === 'flat' && <TrendingFlatIcon sx={{ color: '#ff9800', fontSize: { xs: 18, sm: 24 } }} />}
                <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                  {trend === 'up' && (isMobile ? 'Positiva' : 'Tendência positiva')}
                  {trend === 'down' && (isMobile ? 'Negativa' : 'Tendência negativa')}
                  {trend === 'flat' && (isMobile ? 'Neutra' : 'Tendência neutra')}
                </Typography>
              </Box>
            </Grid>

            {/* Barras de Distribuição */}
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 2 }, fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Distribuição de Sentimentos
              </Typography>
              <SentimentBar
                value={overall.positive}
                label="😊 Positivo"
                color={SENTIMENT_CONFIG.positive.color}
                compact={isMobile}
              />
              <SentimentBar
                value={overall.neutral}
                label="😐 Neutro"
                color={SENTIMENT_CONFIG.neutral.color}
                compact={isMobile}
              />
              <SentimentBar
                value={overall.negative}
                label="😔 Negativo"
                color={SENTIMENT_CONFIG.negative.color}
                compact={isMobile}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filtros - scrollable on mobile */}
      <Box
        sx={{
          mb: { xs: 1.5, sm: 2 },
          overflowX: 'auto',
          mx: { xs: -1, sm: 0 },
          px: { xs: 1, sm: 0 },
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none'
        }}
      >
        <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} sx={{ minWidth: 'max-content' }}>
          <Chip
            label={isMobile ? 'Todos' : 'Todos'}
            onClick={() => setFilter('all')}
            color={filter === 'all' ? 'primary' : 'default'}
            variant={filter === 'all' ? 'filled' : 'outlined'}
            size={isMobile ? 'small' : 'medium'}
            sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
          />
          <Chip
            icon={isMobile ? undefined : <SentimentSatisfiedAltIcon />}
            label={isMobile ? '😊' : 'Positivos'}
            onClick={() => setFilter('positive')}
            sx={{
              bgcolor: filter === 'positive' ? SENTIMENT_CONFIG.positive.bgColor : undefined,
              borderColor: filter === 'positive' ? SENTIMENT_CONFIG.positive.color : undefined,
              fontSize: { xs: '0.7rem', sm: '0.8125rem' }
            }}
            variant={filter === 'positive' ? 'filled' : 'outlined'}
            size={isMobile ? 'small' : 'medium'}
          />
          <Chip
            icon={isMobile ? undefined : <SentimentNeutralIcon />}
            label={isMobile ? '😐' : 'Neutros'}
            onClick={() => setFilter('neutral')}
            sx={{
              bgcolor: filter === 'neutral' ? SENTIMENT_CONFIG.neutral.bgColor : undefined,
              borderColor: filter === 'neutral' ? SENTIMENT_CONFIG.neutral.color : undefined,
              fontSize: { xs: '0.7rem', sm: '0.8125rem' }
            }}
            variant={filter === 'neutral' ? 'filled' : 'outlined'}
            size={isMobile ? 'small' : 'medium'}
          />
          <Chip
            icon={isMobile ? undefined : <SentimentDissatisfiedIcon />}
            label={isMobile ? '😔' : 'Negativos'}
            onClick={() => setFilter('negative')}
            sx={{
              bgcolor: filter === 'negative' ? SENTIMENT_CONFIG.negative.bgColor : undefined,
              borderColor: filter === 'negative' ? SENTIMENT_CONFIG.negative.color : undefined,
              fontSize: { xs: '0.7rem', sm: '0.8125rem' }
            }}
            variant={filter === 'negative' ? 'filled' : 'outlined'}
            size={isMobile ? 'small' : 'medium'}
          />
        </Stack>
      </Box>

      {/* Lista de Segmentos - Híbrida (Paginada + Fullscreen) */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.5, sm: 2 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {isMobile ? `Segmentos (${filteredSegments.length})` : `Segmentos Analisados (${filteredSegments.length})`}
          </Typography>
          <Tooltip title="Abrir em tela cheia">
            <IconButton
              size="small"
              onClick={() => setSentimentFullscreen(true)}
              sx={{ color: '#666', '&:hover': { color: '#1976d2' } }}
            >
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <HybridAnalysisList
          items={filteredSegments}
          renderItem={(segment, index) => (
            <SegmentCard segment={segment} onNavigateToSegment={onNavigateToSegment} compact={isMobile} speakerMapping={speakerMapping} />
          )}
          emptyMessage={isMobile ? 'Nenhum resultado' : 'Nenhum segmento encontrado'}
          emptySubMessage={isMobile ? 'Mude o filtro' : 'Tente selecionar outro filtro de sentimento'}
          onClearFilters={() => setFilter('all')}
          fullscreenTitle="Análise de Sentimento - Todos os Segmentos"
          isFullscreenOpen={sentimentFullscreen}
          onFullscreenClose={() => setSentimentFullscreen(false)}
        />
      </Box>

      {/* Legenda - more compact on mobile */}
      <Box sx={{ mt: { xs: 1.5, sm: 2 }, p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1.5 }}>
          {isMobile ? (
            <>
              💡 Score: -1 (negativo) a +1 (positivo). Toque ➔ para ver na transcrição.
            </>
          ) : (
            <>
              💡 <strong>Como interpretar:</strong> O score varia de -1 (muito negativo) a +1 (muito positivo).
              Valores próximos de 0 indicam sentimento neutro. A análise considera o tom emocional
              das falas, identificando palavras-chave que influenciam o sentimento detectado.
              {onNavigateToSegment && ' Use o botão ➔ para ir diretamente ao segmento na aba Transcrição.'}
            </>
          )}
        </Typography>
      </Box>
    </Box>
  )
}
